import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Loader from "../../components/common/Loader";
import Footer from "../../components/common/Footer";
import Menu from "../../components/common/Menu";
import Navbar from "../../components/common/Navbar";
import StartButton from "../../components/home/StartButton";
import { usePageLoader } from "../../hooks/usePageLoader";
import { useOrdersByEmail } from "../../services/tempeFabricationCachedClient";

// Form validation schema
const findOrderSchema = yup.object().shape({
  email: yup.string().email("Invalid email address").required("Email is required"),
  zipCode: yup.string()
    .required("ZIP code is required")
    .matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format"),
});

interface FindOrderFormData {
  email: string;
  zipCode: string;
}

const FindOrder: React.FC = () => {
  usePageLoader();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState<{ email: string; zipCode: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FindOrderFormData>({
    resolver: yupResolver(findOrderSchema),
  });

  // Use the hook with search params, only enabled when searchParams is set
  const { data, isLoading, error } = useOrdersByEmail(
    searchParams?.email || "",
    searchParams?.zipCode || "",
    !!searchParams // Only fetch when we have search params
  );

  const onSubmit = (formData: FindOrderFormData) => {
    console.log("[FindOrder] Searching for orders with:", formData);
    setSearchParams({
      email: formData.email,
      zipCode: formData.zipCode,
    });
  };

  const orders = data?.results || [];
  const hasSearched = !!searchParams;

  return (
    <>
      <Helmet>
        <title>Find My Order | Tempe Fabrication</title>
        <link rel="stylesheet" type="text/css" href="/home/assets/css/home_1_style.css" />
        <link rel="stylesheet" type="text/css" href="/innerpages/assets/css/innerpages.css" />
      </Helmet>
      <div className="inner-pages-style1">
        <Loader />
        <Menu />
        <div className="smooth-scroll-content" id="scrollsmoother-container">
          <Navbar currentPage="contact" />

          <header className="tc-inner-header-style1">
            <div className="container">
              <div className="info col-lg-8">
                <h2 className="js-title">Find My Order</h2>
              </div>
            </div>
          </header>

          <section className="tc-product-detail-section" style={{ padding: "80px 0" }}>
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-6">
                  <div style={{
                    backgroundColor: "#f9f9f9",
                    padding: "40px",
                    borderRadius: "8px",
                  }}>
                    <div style={{ textAlign: "center", marginBottom: "30px" }}>
                      <i className="fal fa-search" style={{ fontSize: "48px", color: "var(--color-orange1)", marginBottom: "15px" }}></i>
                      <p style={{ color: "#666", lineHeight: "1.6" }}>
                        Enter your email address and shipping ZIP code to find your orders.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="form-group mb-4">
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                          Email Address *
                        </label>
                        <input
                          {...register("email")}
                          type="email"
                          className="form-control"
                          placeholder="your-email@example.com"
                          style={{
                            width: "100%",
                            padding: "12px 15px",
                            border: errors.email ? "1px solid #dc3545" : "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                        />
                        {errors.email && (
                          <span style={{ color: "#dc3545", fontSize: "14px" }}>
                            {errors.email.message}
                          </span>
                        )}
                      </div>

                      <div className="form-group mb-4">
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                          Shipping ZIP Code *
                        </label>
                        <input
                          {...register("zipCode")}
                          type="text"
                          className="form-control"
                          placeholder="12345"
                          style={{
                            width: "100%",
                            padding: "12px 15px",
                            border: errors.zipCode ? "1px solid #dc3545" : "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                        />
                        {errors.zipCode && (
                          <span style={{ color: "#dc3545", fontSize: "14px" }}>
                            {errors.zipCode.message}
                          </span>
                        )}
                      </div>

                      {error && (
                        <div style={{
                          backgroundColor: "#f8d7da",
                          color: "#721c24",
                          padding: "15px",
                          borderRadius: "4px",
                          marginBottom: "20px",
                          fontSize: "14px",
                        }}>
                          {error.message || "Failed to find orders. Please try again."}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="butn border rounded-pill hover-bg-black bg-orange1 text-white"
                        style={{
                          width: "100%",
                          padding: "15px",
                          fontSize: "16px",
                          fontWeight: "600",
                          opacity: isLoading ? 0.6 : 1,
                          cursor: isLoading ? "not-allowed" : "pointer",
                        }}
                      >
                        <span>{isLoading ? "Searching..." : "Find My Orders"}</span>
                      </button>
                    </form>

                    {/* Results section */}
                    {hasSearched && !isLoading && (
                      <div style={{ marginTop: "30px" }}>
                        {orders.length === 0 ? (
                          <div style={{
                            textAlign: "center",
                            padding: "20px",
                            backgroundColor: "#fff3cd",
                            color: "#856404",
                            borderRadius: "4px",
                          }}>
                            <i className="fal fa-inbox" style={{ fontSize: "32px", marginBottom: "10px", display: "block" }}></i>
                            <p style={{ margin: 0 }}>No orders found for the provided email and ZIP code.</p>
                          </div>
                        ) : (
                          <div>
                            <h5 style={{ marginBottom: "15px", fontWeight: "600" }}>
                              Found {orders.length} {orders.length === 1 ? "order" : "orders"}
                            </h5>
                            {orders.map((order) => (
                              <div
                                key={order.id}
                                style={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  padding: "20px",
                                  marginBottom: "15px",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "10px" }}>
                                  <div>
                                    <h6 style={{ margin: "0 0 5px 0", fontWeight: "600" }}>
                                      Order #{order.shortOrderId}
                                    </h6>
                                    <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
                                      {order.customerName}
                                    </p>
                                  </div>
                                  <span style={{
                                    padding: "4px 12px",
                                    borderRadius: "12px",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    backgroundColor: order.status === "DELIVERED" ? "#d4edda" :
                                                     order.status === "SHIPPED" || order.status === "PARTIALLY_SHIPPED" ? "#cce5ff" :
                                                     order.status === "CANCELLED" ? "#f8d7da" :
                                                     "#fff3cd",
                                    color: order.status === "DELIVERED" ? "#155724" :
                                           order.status === "SHIPPED" || order.status === "PARTIALLY_SHIPPED" ? "#004085" :
                                           order.status === "CANCELLED" ? "#721c24" :
                                           "#856404",
                                  }}>
                                    {order.status?.replace(/_/g, " ")}
                                  </span>
                                </div>
                                <p style={{ margin: "5px 0", fontSize: "13px", color: "#999" }}>
                                  Placed on {new Date(order.createdAt!!).toLocaleDateString()}
                                </p>
                                <a
                                  href={`/order-status/${order.id}`}
                                  style={{
                                    color: "var(--color-orange1)",
                                    textDecoration: "none",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                  }}
                                >
                                  View Details →
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{
                      marginTop: "30px",
                      paddingTop: "20px",
                      borderTop: "1px solid #ddd",
                      textAlign: "center",
                    }}>
                      <p style={{ fontSize: "14px", color: "#999", marginBottom: "10px" }}>
                        Already have your order ID?
                      </p>
                      <a
                        href="/order-status"
                        style={{
                          color: "var(--color-orange1)",
                          textDecoration: "none",
                          fontWeight: "500",
                        }}
                      >
                        Check Order Status
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
        <StartButton />
      </div>
    </>
  );
};

export default FindOrder;
